import { useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Table, Tabs, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Download, FileSpreadsheet, FileWarning, Landmark, ReceiptText } from 'lucide-react';
import Layout from '../../Layout';
import { vatReturn, vatReturnXml } from './ktBooks';
import { ktApi, useKt } from './ktStore';
import { dmy, fmtMoney, INPUT_PAY, inputWarnings, type InputInvoice } from './ktLogic';
import { DemoBar, Kpi, PageHeader, Panel, showErrors } from './ktUi';

interface Row { code: string; label: string; value?: number; vat?: number; strong?: boolean }

/** Tờ khai 01/GTGT từ hóa đơn đã xuất và hóa đơn mua vào; cảnh báo khấu trừ từ 5 triệu (SRS 1.4) */
export default function KeToanTaxReturn() {
  const s = useKt();
  const [ym, setYm] = useState(dayjs().subtract(1, 'month').format('YYYY-MM'));
  const [paying, setPaying] = useState<InputInvoice | null>(null);
  const [payRef, setPayRef] = useState('');
  const months = useMemo(() => Array.from(new Set([...s.bills.map(b => b.date.slice(0, 7)), ...s.inputInvoices.map(i => i.date.slice(0, 7))])).sort().reverse(), [s.bills, s.inputInvoices]);
  const r = useMemo(() => vatReturn(s, ym), [s, ym]);
  const inputs = s.inputInvoices.filter(i => i.date.startsWith(ym));
  const flagged = inputs.filter(i => inputWarnings(s.inputInvoices, i).length);

  const rows: Row[] = [
    { code: '23, 24', label: 'Hàng hóa, dịch vụ mua vào trong kỳ', value: r.input.net, vat: r.input.vat },
    { code: '25', label: 'Thuế GTGT được khấu trừ kỳ này', vat: r.deductible },
    { code: '26', label: 'Bán ra không chịu thuế', value: r.buckets.exempt.net },
    { code: '29', label: 'Bán ra thuế suất 0%', value: r.buckets[0].net },
    { code: '30, 31', label: 'Bán ra thuế suất 5%', value: r.buckets[5].net, vat: r.buckets[5].vat },
    { code: '32, 33', label: 'Bán ra thuế suất 10%', value: r.buckets[10].net, vat: r.buckets[10].vat },
    { code: 'PL giảm thuế', label: 'Bán ra thuế suất 8% (chờ xác nhận cách ghi vào 32, 33)', value: r.buckets[8].net, vat: r.buckets[8].vat },
    { code: '34, 35', label: 'Tổng doanh thu và thuế đầu ra', value: r.totalNet, vat: r.outputVat, strong: true },
    { code: '36', label: 'Thuế đầu ra trừ thuế được khấu trừ', vat: r.net36 },
    { code: '40', label: 'Thuế còn phải nộp', vat: r.payable, strong: true },
    { code: '43', label: 'Thuế còn được khấu trừ chuyển kỳ sau', vat: r.carry },
  ];
  const cols: ColumnsType<Row> = [
    { title: 'Chỉ tiêu', dataIndex: 'code', width: 130 },
    { title: 'Nội dung', key: 'l', render: (_, x) => (x.strong ? <b>{x.label}</b> : x.label) },
    { title: 'Giá trị chưa thuế', key: 'v', align: 'right', width: 170, render: (_, x) => (x.value === undefined ? '' : fmtMoney(x.value)) },
    { title: 'Thuế GTGT', key: 't', align: 'right', width: 150, render: (_, x) => (x.vat === undefined ? '' : x.strong ? <b>{fmtMoney(x.vat)}</b> : fmtMoney(x.vat)) },
  ];
  const inputCols: ColumnsType<InputInvoice> = [
    { title: 'Số HĐ', dataIndex: 'no', width: 90 },
    { title: 'Ngày', key: 'd', width: 106, render: (_, i) => dmy(i.date) },
    { title: 'Người bán', key: 'b', render: (_, i) => <div><div>{i.seller}</div><div className="text-[11px] text-gray-500">MST {i.taxCode} · {i.desc}</div></div> },
    { title: 'Gồm VAT', key: 'g', align: 'right', width: 130, render: (_, i) => fmtMoney(i.net + i.vat) },
    { title: 'VAT', key: 'v', align: 'right', width: 110, render: (_, i) => fmtMoney(i.vat) },
    { title: 'Thanh toán', key: 'p', width: 150, render: (_, i) => <div className="text-xs">{INPUT_PAY[i.pay]}{i.payRef && <div className="text-gray-500">{i.payRef}</div>}</div> },
    { title: 'Cảnh báo', key: 'w', width: 280, render: (_, i) => {
      const w = inputWarnings(s.inputInvoices, i);
      return w.length ? w.map(x => <div key={x} className="text-xs text-rose-600">{x}</div>) : <span className="text-xs text-emerald-700">Đủ điều kiện</span>;
    } },
    { title: '', key: 'a', width: 130, align: 'right', render: (_, i) => (i.pay === 'bank' && i.payRef ? null : (
      <Button size="small" onClick={() => { setPayRef(i.payRef); setPaying(i); }}>Ghi chứng từ</Button>
    )) },
  ];

  const download = () => {
    const url = URL.createObjectURL(new Blob([vatReturnXml(r)], { type: 'application/xml' }));
    const a = document.createElement('a');
    a.href = url; a.download = `01GTGT-${ym}-ban-nhap.xml`; a.click();
    URL.revokeObjectURL(url);
    message.success('Đã xuất bản nháp');
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Kê khai thuế GTGT" desc="Số liệu tờ khai lấy từ hóa đơn đã xuất và hóa đơn mua vào, không nhập lại"
          extra={(
            <>
              <Select value={ym} onChange={setYm} className="w-36" aria-label="Kỳ kê khai" options={months.map(m => ({ value: m, label: `Tháng ${m.slice(5)}/${m.slice(0, 4)}` }))} />
              <Button type="primary" icon={<Download size={14} />} onClick={download}>Xuất XML nháp</Button>
            </>
          )} />
        <DemoBar />
        <Alert type="info" showIcon message="XML hiện là bản nháp để đối chiếu số liệu, chưa theo cấu trúc HTKK/eTax nên chưa nộp được. Cách ghi hóa đơn 8% vào chỉ tiêu 32, 33 và phụ lục giảm thuế cần Kế toán thuế xác nhận theo Nghị định 174/2025." />
        {(r.pending.length > 0 || r.needsAdjust.length > 0) && (
          <Alert type="warning" showIcon message={`Kỳ này còn ${r.pending.length} bill chưa xuất hóa đơn và ${r.needsAdjust.length} hóa đơn cần điều chỉnh. Xử lý xong trước khi nộp, nếu không số tờ khai sẽ thiếu.`} />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<ReceiptText size={20} />} tone="bg-sky-50 text-sky-600" label="Thuế đầu ra" value={fmtMoney(r.outputVat)} hint={`${r.issued} hóa đơn đã xuất`} />
          <Kpi icon={<FileSpreadsheet size={20} />} tone="bg-emerald-50 text-emerald-600" label="Thuế được khấu trừ" value={fmtMoney(r.deductible)} hint={`${r.input.count} hóa đơn mua vào`} />
          <Kpi icon={<FileWarning size={20} />} tone="bg-rose-50 text-rose-600" label="Chưa đủ điều kiện khấu trừ" value={r.notDeductible.length} hint={fmtMoney(r.notDeductible.reduce((t, i) => t + i.vat, 0))} />
          <Kpi icon={<Landmark size={20} />} tone="bg-amber-50 text-amber-600" label="Thuế phải nộp" value={fmtMoney(r.payable)} hint={r.carry ? `Chuyển kỳ sau ${fmtMoney(r.carry)}` : undefined} />
        </div>

        <Tabs items={[
          { key: 'return', label: 'Tờ khai 01/GTGT', children: (
            <Panel title={`Tờ khai tháng ${ym.slice(5)}/${ym.slice(0, 4)}`} extra={<Tag>Bản nháp</Tag>}>
              <Table rowKey="code" size="middle" columns={cols} dataSource={rows} pagination={false} scroll={{ x: 760 }} />
            </Panel>
          ) },
          { key: 'input', label: `Hóa đơn mua vào (${inputs.length})`, children: (
            <Panel title={`Hóa đơn mua vào, ${flagged.length} cần xử lý`}>
              <Table rowKey="id" size="middle" columns={inputCols} dataSource={inputs} pagination={false} scroll={{ x: 1100 }} locale={{ emptyText: 'Kỳ này chưa có hóa đơn mua vào' }} />
            </Panel>
          ) },
        ]} />
      </div>
      <Modal open={!!paying} title={`Chứng từ thanh toán HĐ ${paying?.no || ''}`} okText="Lưu" cancelText="Thôi" onCancel={() => setPaying(null)} destroyOnHidden
        onOk={() => { const res = paying ? ktApi.setInputPaid(paying.id, payRef) : { ok: false }; if (res.ok) { message.success('Đã ghi chứng từ'); setPaying(null); } else showErrors(res.errors); }}>
        <label htmlFor="kt-pay-ref" className="text-sm font-medium">Mã giao dịch ngân hàng</label>
        <Input id="kt-pay-ref" value={payRef} onChange={e => setPayRef(e.target.value)} />
      </Modal>
    </Layout>
  );
}
